import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  footer a, 
  footer a:visited, 
  footer a:hover, 
  footer a:active {
    text-decoration: none !important;
    border-bottom: none !important;
    outline: none !important;
    box-shadow: none !important;
  }
`;

const FooterContainer = styled.footer`
  background-color: #000;
  color: #fff;
  padding: 4rem 2rem 2rem;
  margin-top: auto;
  
  a {
    text-decoration: none !important;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: left;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const FooterLogo = styled.div`
  font-weight: 800;
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const FooterNav = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 0.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    justify-content: start;
    max-width: 300px;
    margin: 0;
    text-align: left;
  }
`;

const FooterNavItem = styled.div`
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ff0000;
  }
  
  a, a:link, a:visited, a:hover, a:active {
    text-decoration: none !important;
    color: inherit;
    border-bottom: none !important;
    outline: none !important;
    box-shadow: none !important;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #333;
  color: #fff;
  font-size: 1.2rem;
  transition: background-color 0.3s ease;
  text-decoration: none;
  overflow: hidden;
  
  &:hover {
    background-color: #ff0000;
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: white;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid #333;
`;

const CopyrightBold = styled.div`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const CopyrightText = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 1rem;
`;

const StyledLink = styled(Link)`
  text-decoration: none !important;
  color: white;
  border-bottom: none !important;
  outline: none !important;
  box-shadow: none !important;
`;

const Footer: React.FC = () => {
  return (
    <>
      <GlobalStyle />
      <FooterContainer>
        <FooterContent>
          <FooterSection>
            <FooterLogo>BARKIN SAYIN</FooterLogo>
            <p>"I'm not here to follow – I'm here to lead with vision, build with purpose, and leave a legacy that inspires."</p>
            <SocialLinks>
              <SocialIcon 
                href="https://www.instagram.com/barkinsayin_official/" 
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </SocialIcon>
              <SocialIcon 
                href="https://www.linkedin.com/in/barkinsayin/" 
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </SocialIcon>
              <SocialIcon 
                href="https://www.youtube.com/@BarkinSayin" 
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
              </SocialIcon>
            </SocialLinks>
          </FooterSection>
          
          <FooterSection>
            <h3>Quick Links</h3>
            <FooterNav>
              <FooterNavItem><StyledLink to="/">Home</StyledLink></FooterNavItem>
              <FooterNavItem><StyledLink to="/about">Barkın Sayın</StyledLink></FooterNavItem>
              <FooterNavItem><StyledLink to="/companies">Companies</StyledLink></FooterNavItem>
              <FooterNavItem><StyledLink to="/podcasts">Podcasts</StyledLink></FooterNavItem>
              <FooterNavItem><StyledLink to="/press">Press</StyledLink></FooterNavItem>
              <FooterNavItem><StyledLink to="/achievements">Achievements</StyledLink></FooterNavItem>
              <FooterNavItem><StyledLink to="/events">Events</StyledLink></FooterNavItem>
              <FooterNavItem><StyledLink to="/connect">Connect</StyledLink></FooterNavItem>
            </FooterNav>
          </FooterSection>
          
          <FooterSection>
            <h3>Contact</h3>
            <p>For business inquiries, speaking engagements, or collaboration opportunities, don't hesitate to reach out.</p>
            <StyledLink to="/connect">
              <motion.button 
                className="btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get in Touch
              </motion.button>
            </StyledLink>
          </FooterSection>
        </FooterContent>
        
        <Copyright>
          <CopyrightBold>©️ 2025 Barkin Sayin. All rights reserved.</CopyrightBold>
          <CopyrightText>All content, designs, and brand elements on this website are protected by copyright and trademark laws. Any unauthorized use, reproduction, or distribution is strictly prohibited and will be subject to legal action.</CopyrightText>
        </Copyright>
      </FooterContainer>
    </>
  );
};

export default Footer; 